import { Routes, Route } from "react-router-dom";
import { Suspense, lazy } from "react";
import { ProtectedRoute } from "@/components/molecules/ProtectedRoute";
import { PageSpinner } from "@/components/atoms/PageSpinner";
import { MainLayout } from "@/components/templates/MainLayout";
import { useAuth } from "./hooks/useAuth";
import { AuthBootstrap } from "./Authbootstrap";
import { AdminLayout } from "./components/templates/AdminLayout";
// import { AccountDashboard } from "./pages/account/AccountDashboard";
// ─── Lazy page imports ────────────────────────────────────────────────────────
// useAuth
const PrivacyPage = lazy(() => import("@/pages/legal/PrivacyPage"));
const RefundPolicyPage = lazy(() => import("@/pages/legal/RefundPolicyPage"));
const ShippingPolicyPage = lazy(() => import("@/pages/legal/ShippingPolicyPage"));


const HomePage          = lazy(() => import("@/pages/home/HomePage"));
const CategoryPage      = lazy(() => import("@/pages/category/CategoryPage"));
const ProductDetailPage = lazy(() => import("@/pages/product/ProductDetailPage"));
const SearchPage        = lazy(() => import("@/pages/search/SearchPage"));
const CartPage          = lazy(() => import("@/pages/cart/CartPage"));
const CheckoutPage      = lazy(() => import("@/pages/checkout/CheckoutPage"));
const AccountDashboard  = lazy(() => import("./pages/account/AccountDashboard"));
const OrdersPage        = lazy(() => import("./pages/account/OrdersPage"));
const WishlistPage      = lazy(() => import("@/pages/account/WishlistPage"));
// const AdminDashboard    = lazy(() => import("@/pages/admin/AdminDashboard"));
const LoginPage         = lazy(() => import("@/pages/auth/LoginPage"));
const RegisterPage      = lazy(() => import("@/pages/auth/RegisterPage"));
const NotFoundPage      = lazy(() => import("@/pages/NotFoundPage"));
const ShopPage = lazy(() => import('@/pages/shop/ShopPage'))  


// ─── Lazy admin page imports ──────────────────────────────────────────────────
const AdminDashboardPage = lazy(() => import("@/pages/admin/AdminDashboard"));
const AdminProductsPage  = lazy(() => import("@/pages/admin/ProductsPage"));
const AdminInventoryPage = lazy(() => import("@/pages/admin/InventoryPage"));
const AdminOrdersPage    = lazy(() => import("@/pages/admin/OrdersPage"));
const AdminUsersPage     = lazy(() => import("@/pages/admin/UsersPage"));
const AdminCampaignsPage = lazy(() => import("@/pages/admin/CampaignsAndAuditPages"));
const AdminAuditPage     = lazy(() => import("@/pages/admin/CampaignsAndAuditPages").then(m => ({ default: m.AuditPage })));

// ─── App ─────────────────────────────────────────────────────────────────────

function AccountDashboardRoute() {
  const { user, isLoading } = useAuth()
  if (isLoading) return <PageSpinner />
  if (!user) return null // ProtectedRoute handles the redirect
  return <AccountDashboard profile={user} />
}

export default function App() {
  return (
    <Suspense fallback={<PageSpinner />}>
      <AuthBootstrap>
        <Routes>

          {/* ── Public & authenticated routes — wrapped in MainLayout ──────── */}
          <Route element={<MainLayout />}>
            {/* Public */}
            <Route path="/"                 element={<HomePage />} />
            <Route path="/shop"             element={<ShopPage />} />        // ← category browser
            <Route path="/shop/:category"   element={<CategoryPage />} />
            <Route path="/shop/:cat/:sub"   element={<CategoryPage />} />
            <Route path="/products/:slug"   element={<ProductDetailPage />} />
            <Route path="/search"           element={<SearchPage />} />
            <Route path="/cart"             element={<CartPage />} />
            <Route path="/login"            element={<LoginPage />} />
            <Route path="/register"         element={<RegisterPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/refund-policy" element={<RefundPolicyPage />} />
            <Route path="/shipping-policy" element={<ShippingPolicyPage />} />


            {/* Protected (auth required) */}
            <Route element={<ProtectedRoute />}>
              <Route path="/checkout"            element={<CheckoutPage />} />
              <Route path="/account" element={<AccountDashboardRoute />} />

              <Route path="/account/orders"      element={<OrdersPage />} />
              <Route path="/account/wishlist"    element={<WishlistPage />} />
            </Route>

            {/* 404 — inside MainLayout so header/footer still show */}
            <Route path="*" element={<NotFoundPage />} />
          </Route>

          {/* ── Admin routes ────────────────────────────────────────────────────── */}
          <Route element={<AdminLayout />}>
            <Route element={<ProtectedRoute requiredRole="admin" />}>
              <Route path="/dashboard"            element={<AdminDashboardPage />} />
              <Route path="/dashboard/products"   element={<AdminProductsPage />} />
              <Route path="/dashboard/inventory"  element={<AdminInventoryPage />} />
              <Route path="/dashboard/orders"     element={<AdminOrdersPage />} />
              <Route path="/dashboard/users"      element={<AdminUsersPage />} />
              <Route path="/dashboard/campaigns"  element={<AdminCampaignsPage />} />
              <Route path="/dashboard/audit"      element={<AdminAuditPage />} />
            </Route>
          </Route>

        </Routes>
      </AuthBootstrap>
    </Suspense>
  );
}